const DEVICE_ID_PREVIEW_LENGTH = 8;

export type WebAudioDevice = {
  deviceId: string;
  label: string;
  kind: "audioinput" | "audiooutput";
  groupId?: string;
};

/**
 * Get all available audio input devices
 * Requires user media permission before enumeration
 */
export const getAudioDevices = async (): Promise<WebAudioDevice[]> => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    for (const track of stream.getTracks()) {
      track.stop();
    }

    const devices = await navigator.mediaDevices.enumerateDevices();

    return devices
      .filter((device: MediaDeviceInfo) => device.kind === "audioinput")
      .map((device: MediaDeviceInfo) => ({
        deviceId: device.deviceId,
        label: device.label || `Microphone ${device.deviceId.slice(0, DEVICE_ID_PREVIEW_LENGTH)}`,
        kind: "audioinput" as const,
        groupId: device.groupId,
      }));
  } catch (error) {
    if (error instanceof Error) {
      if (error.name === "NotAllowedError") {
        throw new Error("Microphone permission denied. Please allow microphone access.");
      }
      if (error.name === "NotFoundError") {
        throw new Error("No microphone found. Please connect a microphone.");
      }
    }
    throw new Error("Failed to access audio devices");
  }
};

/**
 * Check if audio devices are supported in this browser
 */
export const isAudioSupported = (): boolean => !!navigator.mediaDevices?.getUserMedia;

/**
 * Request microphone permission
 */
export const requestAudioPermission = async (): Promise<boolean> => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    for (const track of stream.getTracks()) {
      track.stop();
    }
    return true;
  } catch (error) {
    console.error(error);
    return false;
  }
};
